import {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {ExternalLink, Loader2, RefreshCw, Trash2, X} from 'lucide-react';
import {Button} from './Button';
import {Select} from './Select';
import {ConfirmDialog} from './ConfirmDialog';
import {Switch} from './Switch';

// =====================================================================
// Schema 定义 —— 所有预设类型及其字段
// =====================================================================

/** 列/字段的渲染类型 */
type CellType = 'badge' | 'link' | 'switch' | 'text' | 'number' | 'date' | 'image';

/** 表格/列表/卡片的列定义 */
interface ColumnDef {
  key: string;
  label: string;
  /** 渲染类型，默认纯文本 */
  type?: CellType;
  /** badge: 状态→颜色的映射 */
  badge_colors?: Record<string, string>;
  /** link: 链接模板，支持 {key} 占位符，默认用本列值 */
  href_template?: string;
  /** image: 图片宽度（px） */
  image_width?: number;
  /** date: 日期格式化（'relative' | 'datetime'），默认 datetime */
  date_format?: string;
  /** switch: 切换的 API 端点（POST），接收 {key: value} */
  switch_url?: string;
  /** number: 小数位数 */
  decimals?: number;
}

/** 操作的字段收集方式 */
interface ActionPrompt {
  key: string;
  label: string;
  placeholder?: string;
  /** 输入类型：text | number | select | textarea | list */
  input_type?: string;
  /** select 的选项 */
  options?: { label: string; value: string }[];
  /** 多字段弹窗中可选填 */
  optional?: boolean;
  /** textarea：行数（默认 3） */
  rows?: number;
  /** list：每项的标签（用于「添加 xxx」按钮） */
  item_label?: string;
  /** list：每项输入框的占位符 */
  item_placeholder?: string;
}
interface UISchema {
  // ── 通用 ──
  type: 'table' | 'list' | 'stats' | 'cards' | 'playlist' | 'form' | 'composite';
  api?: string;
  /** 页面标题（composite 模式下每个 section 可单独设置） */
  title?: string;
  columns?: ColumnDef[];
  actions?: {
    label: string;
    method: string;
    url: string;
    prompt_field?: ActionPrompt;
    /** 多字段输入弹窗(与 prompt_field 二选一,多字段时优先) */
    prompt_fields?: ActionPrompt[];
    /** 仅行内显示(工具栏隐藏),用于依赖行数据的操作 */
    row_only?: boolean;
    /** 仅工具栏显示(行内隐藏),用于新增类操作 */
    toolbar_only?: boolean;
    show_when?: string;
    /** 从行数据构建 body，如 {"status": "{{row.status}}"} */
    body_template?: Record<string, string>;
  }[];
  fields?: (ColumnDef & { format?: string; subtitle?: string })[];
  /** playlist 专用 */
  status_actions?: { label: string; status: string; icon?: string; show_when?: string }[];
  batch_actions?: { label: string; method: string; url: string; status?: string }[];
  add_action?: { label: string; method: string; url: string; prompt_field: ActionPrompt };

  // ── form 类型专用 ──
  /** 表单提交配置 */
  submit?: { label: string; method: string; url: string };
  // ── composite 类型专用 ──
  /** 子页面列表，每个子页面独立加载数据并渲染 */
  sections?: UISchema[];

  /** form 字段列表 */
  form_fields?: {
    key: string;
    label: string;
    type: 'text' | 'number' | 'select' | 'textarea' | 'switch' | 'password' | 'date';
    placeholder?: string;
    default?: string | number | boolean;
    required?: boolean;
    options?: { label: string; value: string }[];  // select 选项
    rows?: number;  // textarea 行数
  }[];
}

// =====================================================================
// 共享单元格渲染器 —— 所有列表类型共用
// =====================================================================

function renderCell(col: ColumnDef, value: any, row: any, onToggle?: (col: ColumnDef, row: any, newVal: boolean) => void) {
  const val = value ?? '';
  switch (col.type) {
    // ── badge ──
    case 'badge': {
      const str = String(val);
      const colors = col.badge_colors || {};
      const auto = str.includes('完成') || str.includes('done') || str.includes('✅')
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : str.includes('播放') || str.includes('playing') || str.includes('🎵')
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      const cls = colors[str] || auto;
      return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>{str}</span>;
    }
    // ── link ──
    case 'link': {
      const href = col.href_template
        ? col.href_template.replace(/\{(\w+)}/g, (_match: string, key: string) => String(row[key] ?? ''))
        : String(val);
      return (
        <a href={href} target="_blank" rel="noopener noreferrer"
          className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 underline text-xs inline-flex items-center gap-0.5">
          {String(val)} <ExternalLink className="w-3 h-3" />
        </a>
      );
    }
    // ── switch (toggle) ──
    case 'switch': {
      const checked = Boolean(val);
      return (
        <Switch
          checked={checked}
          // 表格行本身可点，开关必须拦掉冒泡否则会连带触发整行动作
          onChange={(next, e) => { e.stopPropagation(); onToggle?.(col, row, next); }}
        />
      );
    }
    // ── number ──
    case 'number': {
      const num = Number(val);
      return <span className="text-xs tabular-nums">{isNaN(num) ? '-' : num.toFixed(col.decimals ?? (Number.isInteger(num) ? 0 : 2))}</span>;
    }
    // ── date ──
    case 'date': {
      const d = new Date(val);
      if (isNaN(d.getTime())) return <span className="text-xs text-gray-400">-</span>;
      if (col.date_format === 'relative') {
        const diff = Date.now() - d.getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return <span className="text-xs">刚刚</span>;
        if (mins < 60) return <span className="text-xs">{mins}分钟前</span>;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return <span className="text-xs">{hours}小时前</span>;
        const days = Math.floor(hours / 24);
        return <span className="text-xs">{days}天前</span>;
      }
      return <span className="text-xs">{d.toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>;
    }
    // ── image ──
    case 'image': {
      const w = col.image_width || 48;
      return <img src={String(val)} alt="" className="rounded object-cover" style={{ width: w, height: w }} />;
    }
    // ── text (inline input) ──
    case 'text':
      return <span className="text-xs break-all">{String(val)}</span>;
    // ── 默认：纯文本 ──
    default:
      return <span className="text-xs">{String(val)}</span>;
  }
}

// =====================================================================
// Props
// =====================================================================

interface Props {
  schema: UISchema;
  pluginName: string;
  /** 插件 UI API 基址,默认 /api/plugin/{name}/ui;多账户模式传入 /api/accounts/{id}/plugin/{name}/ui */
  apiBase?: string;
}

// =====================================================================
// PluginUI 主组件
// =====================================================================

export function PluginUI({ schema, pluginName, apiBase }: Props) {
  const base = apiBase ?? '/api/plugin/' + pluginName + '/ui';

  // 多账户模式:schema 内硬编码的 /api/plugin/{name}/ui/... 重写为账户级 apiBase
  const rewriteUrl = (url: string) => {
    if (apiBase && url.startsWith('/api/plugin/')) {
      const suffix = url.split('/ui')[1] ?? '';
      return apiBase + suffix;
    }
    return url;
  };
  const [data, setData] = useState<any[] | Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [promptAction, setPromptAction] = useState<NonNullable<UISchema['actions']>[number] | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const [promptValues, setPromptValues] = useState<Record<string, string>>({});
  /** list 类型字段的值：key → 多行文本项数组 */
  const [promptLists, setPromptLists] = useState<Record<string, string[]>>({});
  const [promptRow, setPromptRow] = useState<any>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [roomId, setRoomId] = useState<number>(0);
  const [rooms, setRooms] = useState<any[]>([]);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  // Form state
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formInited, setFormInited] = useState(false);
  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMsg, setConfirmMsg] = useState('');
  const [confirmDanger, setConfirmDanger] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);
  // 悬浮提示 —— portal 渲染到 body(fixed 定位),规避滚动容器 overflow 裁剪与行间层叠遮挡
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const showTip = (e: React.MouseEvent, text: string) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.top, text });
  };
  const hideTip = () => setTip(null);

  // ────────────────────────────────────────────────────────────────
  // 数据加载
  // ────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!schema.api) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      let url = rewriteUrl(schema.api);
      if (schema.type === 'playlist' && roomId) url += '?room_id=' + roomId;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const result = await res.json();
      setData(result);
    } catch { /* ignore */ }
    finally { setLoading(false); }
    /* eslint-disable-next-line */
  }, [schema.api, schema.type, roomId, apiBase]);

  const loadRooms = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(base + '/rooms', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const d = await res.json();
      if (Array.isArray(d)) { setRooms(d); if (d.length > 0) setRoomId(prev => prev || d[0].room_id); }
    } catch { /* ignore */ }
    setRoomsLoaded(true);
  }, [pluginName]);

  useEffect(() => {
    if (schema.type === 'composite' || schema.type === 'form') { setLoading(false); return; }
    if (schema.type === 'playlist') { loadRooms(); return; }
    load();
    /* eslint-disable-next-line */
  }, []);
  // 悬浮提示可见期间,任何滚动立即隐藏(避免 fixed 提示停留在旧坐标)
  useEffect(() => {
    if (!tip) return;
    const onScroll = () => setTip(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [tip]);
  useEffect(() => { if (schema.type === 'playlist' && roomId) load(); }, [roomId, load]);
  // Form defaults — init once when form fields change
  useEffect(() => {
    if (schema.type !== 'form' || !schema.form_fields || formInited) return;
    const defaults: Record<string, any> = {};
    schema.form_fields.forEach(f => { defaults[f.key] = f.default ?? (f.type === 'switch' ? false : ''); });
    setFormValues(defaults);
    setFormInited(true);
  }, [schema.type, schema.form_fields, formInited]);

  // ────────────────────────────────────────────────────────────────
  // 操作处理
  // ────────────────────────────────────────────────────────────────

  const closePrompt = () => { setPromptAction(null); setPromptValue(''); setPromptValues({}); setPromptLists({}); setPromptRow(null); };

  const doFetch = async (url: string, method: string, body?: any) => {
    const token = localStorage.getItem('auth_token');
    return fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };

  const submitPrompt = async () => {
    if (!promptAction) return;
    // 单字段(prompt_field)与多字段(prompt_fields)统一收集
    const fields: ActionPrompt[] = promptAction.prompt_fields && promptAction.prompt_fields.length > 0
      ? promptAction.prompt_fields
      : promptAction.prompt_field ? [promptAction.prompt_field] : [];
    if (fields.length === 0) return;
    setActionLoading(promptAction.label);
    try {
      let url = rewriteUrl(promptAction.url);
      if (promptRow) url = url.replace('{id}', promptRow.id || '');
      const body: Record<string, any> = {};
      // 行数据模板（编辑已有条目时需要携带 id 等）
      if (promptAction.body_template) {
        for (const [k, v] of Object.entries(promptAction.body_template)) {
          body[k] = String(v).replace(/\{\{row\.(\w+)}}/g, (_m: string, field: string) => String(promptRow?.[field] ?? ''));
        }
      }
      for (const f of fields) {
        if (f.input_type === 'list') {
          // 多值字段：过滤空项后以数组提交
          body[f.key] = (promptLists[f.key] ?? [])
            .map(s => s.trim())
            .filter(s => s !== '');
        } else {
          const v = promptValues[f.key] ?? '';
          body[f.key] = f.input_type === 'number' ? Number(v) : v;
        }
      }
      await doFetch(url, promptAction.method, body);
      closePrompt();
      await load();
    } catch { /* ignore */ }
    finally { setActionLoading(null); }
  };

  /** 打开输入弹窗：list 字段用行数据预填，普通字段预填字符串 */
  const openPrompt = (action: NonNullable<UISchema['actions']>[number], row?: any) => {
    const fields: ActionPrompt[] = action.prompt_fields && action.prompt_fields.length > 0
      ? action.prompt_fields
      : action.prompt_field ? [action.prompt_field] : [];
    const vals: Record<string, string> = {};
    const lists: Record<string, string[]> = {};
    for (const f of fields) {
      const existing = row?.[f.key];
      if (f.input_type === 'list') {
        lists[f.key] = Array.isArray(existing) && existing.length > 0
          ? existing.map((x: any) => String(x))
          : [''];
      } else {
        vals[f.key] = existing != null ? String(existing) : '';
      }
    }
    setPromptAction(action); setPromptValue(''); setPromptValues(vals);
    setPromptLists(lists); setPromptRow(row || null);
  };

  const handleAction = async (action: NonNullable<UISchema['actions']>[number], row?: any) => {
    if (!action) return;
    if (action.prompt_field || (action.prompt_fields && action.prompt_fields.length > 0)) {
      openPrompt(action, row); return;
    }
    setActionLoading(action.label);
    try {
      let url = rewriteUrl(action.url);
      if (row) url = url.replace('{id}', row.id || '');
      let body: Record<string, string> | undefined;
      if (action.body_template) {
        body = {};
        for (const [k, v] of Object.entries(action.body_template)) {
          body[k] = v.replace(/\{\{row\.(\w+)}}/g, (_match: string, field: string) => String(row[field] ?? ''));
        }
      }
      await doFetch(url, action.method, body);
      await load();
    } catch { /* ignore */ }
    finally { setActionLoading(null); }
  };

  // Switch toggle callback
  const handleSwitchToggle = async (col: ColumnDef, row: any, newVal: boolean) => {
    if (!col.switch_url) return;
    try {
      await doFetch(rewriteUrl(col.switch_url), 'POST', { [col.key]: newVal, id: row.id });
      await load();
    } catch { /* ignore */ }
  };

  // ────────────────────────────────────────────────────────────────
  // 共享渲染
  // ────────────────────────────────────────────────────────────────

  const renderActions = (row?: any) => {
    if (!schema.actions) return null;
    const hasRow = row !== undefined;
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {schema.actions
          .filter(a => {
            if (a.row_only && !hasRow) return false;
            if (a.toolbar_only && hasRow) return false;
            return !a.show_when || Boolean(row?.[a.show_when]);
          })
          .map((action) => (
            <Button key={action.label} variant="ghost" size="sm"
              loading={actionLoading === action.label}
              onClick={() => handleAction(action, row)}>
              {action.label}
            </Button>
          ))}
      </div>
    );
  };

  const renderPromptDialog = () => {
    if (!promptAction) return null;
    // 单字段(prompt_field)与多字段(prompt_fields)共用弹窗
    const fields: ActionPrompt[] = promptAction.prompt_fields && promptAction.prompt_fields.length > 0
      ? promptAction.prompt_fields
      : promptAction.prompt_field ? [promptAction.prompt_field] : [];
    if (fields.length === 0) return null;
    // list 字段：至少一项非空；普通字段：非空（optional 除外）
    const fieldOk = (f: ActionPrompt) => {
      if (f.optional) return true;
      if (f.input_type === 'list') {
        return (promptLists[f.key] ?? []).some(s => s.trim() !== '');
      }
      return (promptValues[f.key] ?? '').trim() !== '';
    };
    const canSubmit = fields.every(fieldOk);
    const hasList = fields.some(f => f.input_type === 'list');
    const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none";
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={closePrompt} />
        <div className={'relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full mx-4 animate-slide-in-up max-h-[85vh] flex flex-col ' + (hasList ? 'max-w-lg' : 'max-w-sm')}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">{promptAction.label}</h3>
            <button onClick={closePrompt} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 space-y-3 overflow-y-auto">
            {fields.map((f, idx) => {
              const isSelect = f.input_type === 'select' && f.options;
              const isList = f.input_type === 'list';
              const isTextarea = f.input_type === 'textarea';
              const items = promptLists[f.key] ?? [];
              const setItems = (next: string[]) => setPromptLists(prev => ({ ...prev, [f.key]: next }));
              return (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{f.label}</label>
                  {isList ? (
                    <div className="space-y-2">
                      {items.map((item, i) => (
                        <div key={i} className="flex gap-2 items-start">
                          <textarea value={item} rows={2}
                            onChange={e => { const arr = [...items]; arr[i] = e.target.value; setItems(arr); }}
                            onKeyDown={e => e.stopPropagation()}
                            placeholder={f.item_placeholder || ''}
                            className={inputCls + ' resize-y font-mono'} />
                          <button
                            onClick={() => { const arr = items.filter((_, j) => j !== i); setItems(arr.length > 0 ? arr : ['']); }}
                            title="删除这一项"
                            className="mt-1.5 p-1.5 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => setItems([...items, ''])}>
                        + 添加{f.item_label || '一项'}
                      </Button>
                    </div>
                  ) : isTextarea ? (
                    <textarea value={promptValues[f.key] ?? ''} rows={f.rows || 3}
                      onChange={e => setPromptValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                      onKeyDown={e => {
                        // 回车用于换行（阻止冒泡，避免被外层的提交逻辑截获）
                        e.stopPropagation();
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing && (e.ctrlKey || e.metaKey) && canSubmit) submitPrompt();
                      }}
                      placeholder={(f.placeholder || '') + '（回车换行，Ctrl+Enter 提交）'}
                      className={inputCls + ' resize-y'} autoFocus={idx === 0} />
                  ) : isSelect ? (
                    <Select
                      value={promptValues[f.key] ?? ''}
                      onChange={v => setPromptValues(prev => ({ ...prev, [f.key]: v }))}
                      placeholder="-- 请选择 --"
                      ariaLabel={f.label}
                      className="w-full"
                      options={f.options!.map((o: { label: string; value: string }) => ({
                        value: o.value, label: o.label,
                      }))}
                    />
                  ) : (
                    <input type={f.input_type || 'text'} value={promptValues[f.key] ?? ''}
                      onChange={(e) => setPromptValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder || ''}
                      className={inputCls}
                      onKeyDown={(e) => {
                        // isComposing：中文输入法确认候选词的回车不应触发提交
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing && canSubmit) submitPrompt();
                      }} autoFocus={idx === 0} />
                  )}
                </div>
              );
            })}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={closePrompt}>取消</Button>
              <Button variant="primary" size="sm" onClick={submitPrompt}
                loading={actionLoading === promptAction.label} disabled={!canSubmit}>确定</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Unwrap data
  const unwrap = (d: any): any[] | null => {
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object') {
      if (Array.isArray(d.items)) return d.items;
      if (Array.isArray(d.data)) return d.data;
    }
    return null;
  };

  // ────────────────────────────────────────────────────────────────
  // 加载状态
  // ────────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!schema.type) return <p className="text-sm text-gray-400 py-4">UI Schema 缺少 type 字段</p>;
  // Composite & form types don't require api/data at top level
  if (!data && schema.type !== 'form' && schema.type !== 'composite') {
    return <p className="text-sm text-gray-400 py-4">暂无数据</p>;
  }

  const listData = data ? unwrap(data) : null;
  const qs = (extra: Record<string, string> = {}) => '?' + new URLSearchParams({ room_id: String(roomId), ...extra }).toString();

  // ==================================================================
  // Table（桌面端表格 / 移动端自动切换为卡片列表）
  // ==================================================================
  if (schema.type === 'table' && schema.columns) {
    const rows = listData || [];
    return (
      <div className="space-y-3">{renderPromptDialog()}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" icon={<RefreshCw />} onClick={load}>刷新</Button>
          <span className="text-xs text-gray-400">{rows.length} 条</span>
          {renderActions()}
        </div>

        {/* 桌面端：表格（sm 及以上） */}
        <div className="overflow-x-auto hidden sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                {schema.columns.map((col) => (
                  <th key={col.key} className="text-left px-3 py-2 text-xs font-medium text-gray-500">{col.label}</th>
                ))}
                {schema.actions && <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">操作</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {rows.length === 0 ? (
                <tr><td colSpan={(schema.columns.length || 1) + (schema.actions ? 1 : 0)} className="px-3 py-4 text-center text-gray-400">暂无数据</td></tr>
              ) : rows.map((row: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  {schema.columns!.map((col) => (
                    <td key={col.key} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {renderCell(col, row[col.key], row, handleSwitchToggle)}
                    </td>
                  ))}
                  {schema.actions && <td className="px-3 py-2 text-right">{renderActions(row)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 移动端：卡片列表（sm 以下），每行一卡，标签+值纵向展示 */}
        <div className="space-y-2 sm:hidden">
          {rows.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">暂无数据</p>
          ) : rows.map((row: any, i: number) => (
            <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
              <div className="space-y-1.5">
                {schema.columns!.map((col) => (
                  <div key={col.key} className="flex items-start justify-between gap-3">
                    <span className="text-xs text-gray-500 shrink-0">{col.label}</span>
                    <span className="text-right text-sm text-gray-700 dark:text-gray-300 break-words min-w-0">
                      {renderCell(col, row[col.key], row, handleSwitchToggle)}
                    </span>
                  </div>
                ))}
              </div>
              {schema.actions && (
                <div className="mt-2.5 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-1 justify-end">
                  {renderActions(row)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================================================================
  // List
  // ==================================================================
  if (schema.type === 'list' && schema.columns) {
    const items = listData || [];
    return (
      <div className="space-y-2">{renderPromptDialog()}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" icon={<RefreshCw />} onClick={load}>刷新</Button>
          {renderActions()}
        </div>
        <div className="space-y-1.5">
          {items.map((item: any, i: number) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center gap-3 flex-wrap">
                {schema.columns!.map((col) => renderCell(col, item[col.key], item, handleSwitchToggle))}
              </div>
              {renderActions(item)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================================================================
  // Cards
  // ==================================================================
  if (schema.type === 'cards' && schema.columns) {
    const cards = listData || [];
    return (
      <div className="space-y-3">{renderPromptDialog()}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" icon={<RefreshCw />} onClick={load}>刷新</Button>
          {renderActions()}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cards.map((item: any, i: number) => (
            <div key={i} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
              {schema.columns!.map((col) => (
                <div key={col.key} className="flex justify-between py-0.5">
                  <span className="text-xs text-gray-500">{col.label}</span>
                  {renderCell(col, item[col.key], item, handleSwitchToggle)}
                </div>
              ))}
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">{renderActions(item)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================================================================
  // Stats
  // ==================================================================
  if (schema.type === 'stats' && data && !Array.isArray(data) && schema.fields) {
    const stats = data as Record<string, any>;
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" icon={<RefreshCw />} onClick={load}>刷新</Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
          {schema.fields.map((field) => (
            <div key={field.key} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900">
              <p className="text-[10px] text-gray-500 uppercase">{field.label}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                {field.format === 'duration'
                  ? `${Math.floor(Number(stats[field.key] || 0) / 60)}分${Number(stats[field.key] || 0) % 60}秒`
                  : field.type && field.type !== 'text'
                  ? renderCell(field, stats[field.key], stats, handleSwitchToggle)
                  : String(stats[field.key] ?? '-')}
              </p>
              {field.subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{field.subtitle}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================================================================
  // Playlist
  // ==================================================================
  if (schema.type === 'playlist') {
    const items = listData || [];
    const statsMap: Record<string, number> = {};
    items.forEach((i: any) => { const s = i.status || 'pending'; statsMap[s] = (statsMap[s] || 0) + 1; });

    const toggleSel = (i: number) => { const n = new Set(selected); n.has(i) ? n.delete(i) : n.add(i); setSelected(n); };
    const clearSel = () => setSelected(new Set());
    const batchApi = async (st: string) => {
      for (const i of [...selected].sort((a, b) => b - a))
        await doFetch(base + '/status' + qs(), 'POST', { index: i, status: st });
      clearSel(); await load();
    };
    const showConfirm = (title: string, msg: string, cb: () => void, danger = false) => {
      setConfirmTitle(title); setConfirmMsg(msg); setConfirmDanger(danger);
      setConfirmCallback(() => cb); setConfirmOpen(true);
    };
    const batchDelete = () => {
      showConfirm('批量删除', '确定删除选中的 ' + selected.size + ' 项？', async () => {
        for (const i of [...selected].sort((a, b) => b - a))
          await doFetch(base + '/delete' + qs(), 'POST', { index: i });
        clearSel(); await load();
      }, true);
    };
    const clearAll = () => {
      showConfirm('清空点播单', '确定清空当前直播间的全部点播项吗？此操作不可恢复。', async () => {
        await doFetch(base + '/clear' + qs(), 'POST');
        clearSel(); await load();
      }, true);
    };

    const statusIcons: Record<string, string> = { pending: '⏳', playing: '🎵', working: '🔧', done: '✅' };
    const statusLabels: Record<string, string> = { pending: '待播', playing: '播放中', working: '操作中', done: '已完成' };

    const plSubmitPrompt = async () => {
      if (!promptAction || !promptAction.prompt_field) return;
      setActionLoading(promptAction.label);
      try {
        const token = localStorage.getItem('auth_token');
        const body: Record<string, any> = {};
        body[promptAction.prompt_field.key] = promptValue;
        await fetch(rewriteUrl(promptAction.url) + qs(), {
          method: promptAction.method,
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(body),
        });
        closePrompt(); await load();
      } catch { /* ignore */ }
      finally { setActionLoading(null); }
    };

    return (
      <div className="space-y-3">
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          {rooms.length > 1 && (
            <Select
              value={String(roomId)}
              onChange={v => setRoomId(Number(v))}
              size="sm"
              ariaLabel="选择直播间"
              className="w-full sm:w-auto"
              options={rooms.map((r: any) => ({
                value: String(r.room_id), label: `${r.room_name} (${r.count}条)`,
              }))}
            />
          )}
          {rooms.length === 1 && <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{rooms[0].room_name}</span>}
          <Button variant="ghost" size="sm" icon={<RefreshCw />} onClick={load}>刷新</Button>
          <span className="text-xs text-gray-400">{items.length} 条</span>
          {schema.add_action && (
            <Button variant="primary" size="sm" onClick={() => {
              setPromptAction(schema.add_action!); setPromptValue(''); setPromptRow(null);
            }}>{schema.add_action.label}</Button>
          )}
          {items.length > 0 && (
            <Button variant="ghost" size="sm" icon={<Trash2 />} className="text-red-500!"
              onClick={clearAll}>清空</Button>
          )}
        </div>

        {/* Stats */}
        <div className="flex gap-2 flex-wrap">
          {Object.entries(statusLabels).map(([k, v]) =>
            statsMap[k] ? <span key={k} className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{statusIcons[k]} {v} {statsMap[k]}</span> : null
          )}
        </div>

        {/* Items + batch bar (scrollable container) */}
        <div className="relative max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="space-y-1 p-1 min-h-[120px]">
            {!roomsLoaded ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : rooms.length === 0 ? (
              <p className="text-center text-gray-400 py-8">📭 暂无直播间数据<br /><small className="text-xs">在直播间发送弹幕后将自动出现</small></p>
            ) : items.length === 0 ? (
              <p className="text-center text-gray-400 py-8">📭 点播单为空</p>
            ) : items.map((item: any, i: number) => {
            const st = item.status || 'pending';
            let rowCls = '';
            if (st === 'playing') rowCls = 'border-emerald-400/50 bg-emerald-50/30 dark:bg-emerald-900/10';
            else if (st === 'working') rowCls = 'border-blue-400/50 bg-blue-50/30 dark:bg-blue-900/10';
            else if (st === 'done') rowCls = 'opacity-50';
            let statusCls = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
            if (st === 'playing') statusCls = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
            else if (st === 'working') statusCls = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            else if (st === 'done') statusCls = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';

            // 状态徽章 / 操作按钮组（桌面端与移动端共用）
            const statusBadge = (
              <span className={'text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ' + statusCls}>{statusLabels[st]}</span>
            );
            const statusButtons = (
              <div className="flex gap-0.5">
                {/* 悬浮提示由 portal 渲染到 body(fixed + 最高层级),不受本滚动容器裁剪/遮挡 */}
                {['playing', 'working', 'done', 'pending'].map(s => {
                  const btnCls = 'px-2 py-1.5 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' + (s === st ? ' opacity-30 cursor-default' : '');
                  return <button key={s} disabled={s === st} className={btnCls}
                    onMouseEnter={e => showTip(e, statusLabels[s])} onMouseLeave={hideTip}
                    onClick={async () => { if (s === st) return; await doFetch(base + '/status' + qs(), 'POST', { index: i, status: s }); await load(); }}>
                    {statusIcons[s]}
                  </button>
                })}

                <button
                  className="px-2 py-1.5 text-sm rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                  onMouseEnter={e => showTip(e, '删除')} onMouseLeave={hideTip}
                  onClick={async () => {
                    if (!confirm('确定删除 #' + (item.index || i + 1) + '「' + item.song_name + '」？')) return;
                    await doFetch(base + '/delete' + qs(), 'POST', { index: i });
                    setSelected(prev => { const n = new Set(prev); n.delete(i); return n; }); await load();
                  }}>✕</button>
              </div>
            );

            return (
              <div key={i}>
                {/* 桌面端行（sm 及以上） */}
                <div className={'hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 ' + rowCls}>
                  <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSel(i)}
                    className="w-4 h-4 rounded accent-primary-500 cursor-pointer" />
                  <span className="text-xs font-bold text-gray-400 w-7 text-center">#{item.index || i + 1}</span>
                  {schema.columns?.map(col => {
                    const spanCls = col.key === 'song_name' ? 'flex-1 text-sm font-medium text-gray-800 dark:text-gray-200' : 'text-xs text-gray-400';
                    return <span key={col.key} className={spanCls}>
                      {col.key === 'status'
                        ? statusBadge
                        : col.type ? renderCell(col, item[col.key], item, handleSwitchToggle)
                        : String(item[col.key] ?? '-')}
                    </span>
                  })}
                  {statusButtons}
                </div>
                {/* 移动端卡片（sm 以下）：第一行勾选+序号+歌名+状态，第二行点播者+操作按钮 */}
                <div className={'flex sm:hidden flex-col gap-2 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 ' + rowCls}>
                  <div className="flex items-center gap-2 min-w-0">
                    <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSel(i)}
                      className="w-4 h-4 shrink-0 rounded accent-primary-500 cursor-pointer" />
                    <span className="text-xs font-bold text-gray-400 w-7 shrink-0 text-center">#{item.index || i + 1}</span>
                    <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{item.song_name}</span>
                    {statusBadge}
                  </div>
                  <div className="flex items-center justify-between gap-2 pl-11">
                    <span className="text-xs text-gray-400 truncate">{item.user_name || '—'}</span>
                    {statusButtons}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {/* Batch toolbar — 独立于列表滚动容器，始终显示在列表下方，不遮挡任何条目 */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <span className="text-xs font-medium text-primary-700 dark:text-primary-300">已选 {selected.size} 项</span>
            <Button variant="ghost" size="sm" onClick={() => batchApi('playing')}>🎵<span className="sm:hidden ml-1">播放中</span></Button>
            <Button variant="ghost" size="sm" onClick={() => batchApi('working')}>🔧<span className="sm:hidden ml-1">操作中</span></Button>
            <Button variant="ghost" size="sm" onClick={() => batchApi('done')}>✅<span className="sm:hidden ml-1">已完成</span></Button>
            <Button variant="ghost" size="sm" onClick={() => batchApi('pending')}>🔄<span className="sm:hidden ml-1">待播</span></Button>
            <Button variant="ghost" size="sm" className="text-red-500!" onClick={batchDelete}>🗑️ 删除</Button>
            <Button variant="ghost" size="sm" onClick={clearSel}>取消</Button>
          </div>
        )}

        {/* Prompt dialog */}
        {promptAction && promptAction.prompt_field && (
          (() => { const pf = promptAction.prompt_field!; return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center">
              <div className="fixed inset-0 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={closePrompt} />
              <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 animate-slide-in-up">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{promptAction.label}</h3>
                  <button onClick={closePrompt} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{pf.label}</label>
                  <input type={pf.input_type || 'text'} value={promptValue} onChange={e => setPromptValue(e.target.value)}
                    placeholder={pf.placeholder || ''}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) plSubmitPrompt();
                    }} autoFocus />
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" size="sm" onClick={closePrompt}>取消</Button>
                    <Button variant="primary" size="sm" onClick={plSubmitPrompt} loading={actionLoading === promptAction.label} disabled={!promptValue.trim()}>确定</Button>
                  </div>
                </div>
              </div>
            </div>
          )})()
        )}

        {/* Confirm dialog */}
        {confirmCallback && (
          <ConfirmDialog open={confirmOpen} title={confirmTitle} message={confirmMsg}
            danger={confirmDanger}
            onConfirm={() => { confirmCallback?.(); setConfirmOpen(false); setConfirmCallback(null); }}
            onCancel={() => { setConfirmOpen(false); setConfirmCallback(null); }} />
        )}

        {/* 悬浮提示 —— portal 到 body(fixed + z-100),滚动容器无法裁剪、任何行无法遮挡 */}
        {tip && createPortal(
          <div className="pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-full"
            style={{ left: tip.x, top: tip.y - 6 }}>
            <div className="px-2 py-1 rounded-md text-[11px] font-medium whitespace-nowrap
                            bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-lg">
              {tip.text}
            </div>
          </div>,
          document.body,
        )}
      </div>
    );
  }

  // ==================================================================
  // Form
  // ==================================================================
  if (schema.type === 'form' && schema.form_fields) {
    const updateField = (key: string, value: any) => setFormValues(prev => ({ ...prev, [key]: value }));

    const handleSubmit = async () => {
      if (!schema.submit) return;
      setFormSubmitting(true);
      try {
        await doFetch(rewriteUrl(schema.submit.url), schema.submit.method, formValues);
        // Reset to defaults on success
        const defaults: Record<string, any> = {};
        schema.form_fields!.forEach(f => { defaults[f.key] = f.default ?? (f.type === 'switch' ? false : ''); });
        setFormValues(defaults);
      } catch { /* ignore */ }
      finally { setFormSubmitting(false); }
    };

    const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50";

    return (
      <div className="space-y-4 max-w-lg">
        {schema.form_fields.map(f => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {f.label}
              {f.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>

            {/* text / password */}
            {(f.type === 'text' || f.type === 'password') && (
              <input type={f.type} value={formValues[f.key] ?? ''} onChange={e => updateField(f.key, e.target.value)}
                placeholder={f.placeholder || ''} required={f.required}
                className={inputCls} />
            )}

            {/* number */}
            {f.type === 'number' && (
              <input type="number" value={formValues[f.key] ?? ''} onChange={e => updateField(f.key, Number(e.target.value))}
                placeholder={f.placeholder || ''} required={f.required}
                className={inputCls} />
            )}

            {/* date */}
            {f.type === 'date' && (
              <input type="date" value={formValues[f.key] ?? ''} onChange={e => updateField(f.key, e.target.value)}
                required={f.required} className={inputCls} />
            )}

            {/* textarea */}
            {f.type === 'textarea' && (
              <textarea value={formValues[f.key] ?? ''} onChange={e => updateField(f.key, e.target.value)}
                placeholder={f.placeholder || ''} rows={f.rows || 3} required={f.required}
                className={inputCls + ' resize-y'} />
            )}

            {/* select */}
            {f.type === 'select' && f.options && (
              <Select
                value={formValues[f.key] ?? ''}
                onChange={v => updateField(f.key, v)}
                placeholder="-- 请选择 --"
                ariaLabel={f.label}
                className="w-full"
                options={f.options.map((o: { label: string; value: string }) => ({
                  value: o.value, label: o.label,
                }))}
              />
            )}

            {/* switch */}
            {f.type === 'switch' && (
              <Switch
                checked={!!formValues[f.key]}
                onChange={(next) => updateField(f.key, next)}
                label={f.label || f.key}
              />
            )}
          </div>
        ))}

        {schema.submit && (
          <div className="flex justify-end pt-2">
            <Button variant="primary" size="sm" className="w-full sm:w-auto" onClick={handleSubmit} loading={formSubmitting}>
              {schema.submit.label}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ==================================================================
  // Composite —— 多类型复合页面
  // ==================================================================
  if (schema.type === 'composite' && schema.sections) {
    return (
      <div className="space-y-6">
        {schema.sections.map((sec, idx) => (
          <div key={idx}>
            {sec.title && (
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                {sec.title}
              </h3>
            )}
            <PluginUI schema={sec} pluginName={pluginName} apiBase={apiBase} />
          </div>
        ))}
      </div>
    );
  }

  // ==================================================================
  // Fallback
  // ==================================================================
  return <p className="text-sm text-gray-400 py-4">不支持的 UI Schema 类型: {schema.type}</p>;
}
